/**
 * Education ID Mappings for Frontend
 *
 * Maps display names to stable IDs for API communication.
 * These IDs match the backend educationIds.js and studyIds.js
 */

// ============================================================================
// Level Mappings
// ============================================================================

/**
 * Level display name → ID mapping
 * Used when sending search requests to the API
 */
export const LEVEL_IDS = {
  // MBO Levels (4 levels)
  'MBO Niveau 1': 'MBO_NIVEAU_1',
  'MBO Niveau 2': 'MBO_NIVEAU_2',
  'MBO Niveau 3': 'MBO_NIVEAU_3',
  'MBO Niveau 4': 'MBO_NIVEAU_4',

  // HBO Levels (3 levels)
  'HBO Associate Degree': 'HBO_ASSOCIATE_DEGREE',
  'HBO Bachelor': 'HBO_BACHELOR',
  'HBO Master': 'HBO_MASTER',

  // University Levels (2 levels)
  'Universiteit Bachelor': 'UNIVERSITEIT_BACHELOR',
  'Universiteit Master': 'UNIVERSITEIT_MASTER'
};

/**
 * Level ID → display name mapping
 * Used when displaying vacancy education requirements
 */
export const LEVEL_NAMES = Object.fromEntries(
  Object.entries(LEVEL_IDS).map(([name, id]) => [id, name])
);

/**
 * Education type by level ID
 * Used for grouping and filtering
 */
export const LEVEL_TYPES = {
  'MBO_NIVEAU_1': 'MBO',
  'MBO_NIVEAU_2': 'MBO',
  'MBO_NIVEAU_3': 'MBO',
  'MBO_NIVEAU_4': 'MBO',
  'HBO_ASSOCIATE_DEGREE': 'HBO',
  'HBO_BACHELOR': 'HBO',
  'HBO_MASTER': 'HBO',
  'UNIVERSITEIT_BACHELOR': 'UNIVERSITEIT',
  'UNIVERSITEIT_MASTER': 'UNIVERSITEIT'
};

// ============================================================================
// Study ID Generation
// ============================================================================

/**
 * Generate a study ID from level ID and study name
 * Matches the backend generateStudyId function
 *
 * @param {string} levelId - Level ID (e.g., "HBO_BACHELOR")
 * @param {string} studyName - Study display name (e.g., "Finance & Control")
 * @returns {string} Study ID (e.g., "HBO_BACHELOR_FINANCE_AND_CONTROL")
 */
export function generateStudyId(levelId, studyName) {
  if (!levelId || !studyName) return null;

  // Normalize the study name to create a valid ID
  const normalizedName = studyName
    .toUpperCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')  // Remove diacritics (é → e)
    .replace(/\s+/g, '_')              // spaces → underscores
    .replace(/&/g, 'AND')              // & → AND
    .replace(/\//g, '_OR_')            // / → _OR_
    .replace(/[()]/g, '')              // remove parentheses
    .replace(/[,.':-]/g, '')           // remove punctuation
    .replace(/_+/g, '_')               // collapse multiple underscores
    .replace(/^_|_$/g, '');            // trim leading/trailing underscores

  return `${levelId}_${normalizedName}`;
}

/**
 * Get study ID from level name and study name
 *
 * @param {string} levelName - Level display name (e.g., "HBO Bachelor")
 * @param {string} studyName - Study display name (e.g., "Finance & Control")
 * @returns {string|null} Study ID or null if level not found
 */
export function getStudyId(levelName, studyName) {
  const levelId = LEVEL_IDS[levelName];
  if (!levelId) return null;
  return generateStudyId(levelId, studyName);
}

/**
 * Get level ID from level name
 *
 * @param {string} levelName - Level display name (e.g., "HBO Bachelor")
 * @returns {string|null} Level ID or null if not found
 */
export function getLevelId(levelName) {
  return LEVEL_IDS[levelName] || null;
}

/**
 * Get level name from level ID
 *
 * @param {string} levelId - Level ID (e.g., "HBO_BACHELOR")
 * @returns {string|null} Level display name or null if not found
 */
export function getLevelName(levelId) {
  return LEVEL_NAMES[levelId] || null;
}

/**
 * Get education type from level ID
 *
 * @param {string} levelId - Level ID (e.g., "HBO_BACHELOR")
 * @returns {string|null} Education type ("MBO", "HBO", or "UNIVERSITEIT")
 */
export function getEducationType(levelId) {
  return LEVEL_TYPES[levelId] || null;
}

/**
 * Check if level ID is valid
 *
 * @param {string} levelId - Level ID to validate
 * @returns {boolean}
 */
export function isValidLevelId(levelId) {
  return levelId in LEVEL_NAMES;
}

/**
 * Parse a study ID to extract level ID and normalized name
 *
 * @param {string} studyId - Study ID
 * @returns {Object|null} { levelId, normalizedName } or null
 */
export function parseStudyId(studyId) {
  if (!studyId) return null;

  const levelIds = Object.values(LEVEL_IDS);
  const sortedLevelIds = levelIds.sort((a, b) => b.length - a.length);

  for (const levelId of sortedLevelIds) {
    if (studyId.startsWith(`${levelId}_`)) {
      return {
        levelId,
        normalizedName: studyId.slice(levelId.length + 1)
      };
    }
  }

  return null;
}

// ============================================================================
// Year Utilities
// ============================================================================

/**
 * Convert year display name to number
 *
 * @param {string} yearName - Year display name (e.g., "Jaar 3")
 * @returns {number|null} Year number (1-4) or null
 */
export function parseYearName(yearName) {
  if (!yearName) return null;
  const match = yearName.match(/Jaar\s*(\d+)/i);
  if (match) {
    const num = parseInt(match[1], 10);
    return num >= 1 && num <= 4 ? num : null;
  }
  return null;
}

/**
 * Convert year number to display name
 *
 * @param {number} year - Year number (1-4)
 * @returns {string} Year display name (e.g., "Jaar 3")
 */
export function formatYearName(year) {
  return `Jaar ${year}`;
}

export default {
  LEVEL_IDS,
  LEVEL_NAMES,
  LEVEL_TYPES,
  generateStudyId,
  getStudyId,
  getLevelId,
  getLevelName,
  getEducationType,
  isValidLevelId,
  parseStudyId,
  parseYearName,
  formatYearName
};
